import { useCancelDeleteCipOrderLinesMutation } from "@saleor/graphql";
import useNotifier from "@saleor/hooks/useNotifier";
import getErrorOutDated from "@saleor/utils/errors/outDated";
import { FormikProps } from "formik";
import { Dispatch, SetStateAction } from "react";

interface Props {
  formikItemDetails: FormikProps<{ lines: NotRefOrderLine[] }>;
  orderId: string;
  selected: string[];
  refetchChangeOrderData?: () => void;
  setRefetchInfo?: Dispatch<SetStateAction<any>>;
  setIsSynced?: (e: boolean) => void;
  setOpenModalConfirmCancel?: (e: boolean) => void;
  clearSelected: () => void;
  setSubmitState: Dispatch<SetStateAction<any>>;
  totalItemsOriginal?: number;
}

function useCancelDeleteItem({
  formikItemDetails,
  orderId,
  selected,
  setOpenModalConfirmCancel,
  clearSelected,
  refetchChangeOrderData,
  setSubmitState,
  totalItemsOriginal
}: Props) {
  const notify = useNotifier();
  const [
    cancelDeleteItem,
    { loading: loadingCancelDeleteItem }
  ] = useCancelDeleteCipOrderLinesMutation();
  const lines = formikItemDetails?.values?.lines;
  const confirmCancelItem = reason => {
    const selectedOrderLine = [];
    const newListLineOrder = lines.map(item => {
      if (selected.includes(item.itemNo)) {
        selectedOrderLine.push({
          itemNo: item.itemNo,
          status: reason
        });
        return {
          ...item,
          statusCancel: reason
        };
      }
      return item;
    });
    cancelDeleteItem({
      variables: {
        soNo: orderId,
        orderLines: selectedOrderLine,
        totalItems: totalItemsOriginal
      }
    }).then(({ data }) => {
      if (data?.cancelDeleteCipOrderLines?.warningMessages?.length) {
        const fmtMessage = item => {
          const title = item.order || item.itemNo;
          return title ? `${title} - ${item.message}` : item.message;
        };
        notify({
          title: "Warning",
          status: "warning",
          apiMessage: data?.cancelDeleteCipOrderLines?.warningMessages
            ?.map(fmtMessage)
            .join("\n"),
          autohide: null
        });
      }
      if (data?.cancelDeleteCipOrderLines?.status) {
        refetchChangeOrderData();
        setOpenModalConfirmCancel(false);
        clearSelected();
      } else {
        const errorOutDated = getErrorOutDated(data?.cancelDeleteCipOrderLines);
        if (errorOutDated) {
          const responseData = {
            ...data?.cancelDeleteCipOrderLines,
            errorOutDated
          };
          setSubmitState({
            state: "errorPopup",
            context: responseData
          });
          setOpenModalConfirmCancel(false);
          clearSelected();
        } else {
          newListLineOrder.forEach(item => {
            const listFailUpdate = [
              ...data.cancelDeleteCipOrderLines.sapItemMessages.map(
                line => line.itemNo
              )
            ];
            return {
              ...item,
              statusCancel: listFailUpdate.includes(item.itemNo)
                ? ""
                : "item.statusCancel"
            };
          });

          const {
            sapOrderMessages,
            sapItemMessages
          } = data.cancelDeleteCipOrderLines;
          if (sapOrderMessages?.length || sapItemMessages?.length) {
            setSubmitState({
              state: "errorPopup",
              context: data?.cancelDeleteCipOrderLines
            });
          } else {
            const errors = data?.cancelDeleteCipOrderLines?.errors;
            if (errors.length > 0) {
              notify({
                status: "error",
                title: "Something went wrong",
                text: errors.map(
                  ({ code, message }) => `${code || ""} - ${message}`
                )
              });
            }
          }
        }
      }
    });
  };

  return {
    confirmCancelItem,
    loadingCancelDeleteItem
  };
}
export default useCancelDeleteItem;
